import { Group, NumberInput } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { NumberRangeFilterValue } from "./filterFns";
import type { MeqTableLocalization } from "./types";

/**
 * Header-row filter UI for number columns using `filterVariant: "range"`
 * (ported from the old wrapper's `numberFilter`/`filterVariant: "range"`
 * pairing). Column filters stay off by
 * default (`hasColumnFilters`); this only renders when a caller opts in and
 * only for range-variant columns (the only filterVariant in use across the
 * codebase).
 *
 * `filterMin`/`filterMax` default from i18next `table.*` (parity with MRT's
 * own `min`/`max` locale strings) and double as both placeholder and
 * aria-label for each input, overridable via `localization`.
 */

export interface NumberRangeFilterProps {
  value: NumberRangeFilterValue | undefined;
  onChange: (value: NumberRangeFilterValue) => void;
  localization?: Partial<MeqTableLocalization>;
}

const NumberRangeFilter = ({
  value,
  onChange,
  localization,
}: NumberRangeFilterProps) => {
  const { t } = useTranslation();
  const [min, max] = value ?? [null, null];
  const minLabel = localization?.filterMin ?? t("table.filterMin");
  const maxLabel = localization?.filterMax ?? t("table.filterMax");

  return (
    <Group gap={4} wrap="nowrap">
      <NumberInput
        size="xs"
        w={72}
        placeholder={minLabel}
        aria-label={minLabel}
        value={min ?? ""}
        onChange={(nextMin) => onChange([nextMin === "" ? null : nextMin, max])}
      />
      <NumberInput
        size="xs"
        w={72}
        placeholder={maxLabel}
        aria-label={maxLabel}
        value={max ?? ""}
        onChange={(nextMax) => onChange([min, nextMax === "" ? null : nextMax])}
      />
    </Group>
  );
};

export default NumberRangeFilter;
